/**
 * The level agent (headless): loads a level onto a board, checks the goal checklist
 * against the live simulation, and picks hints. Rendering/coach react to its results.
 */
import { Board, footprint } from '../core/board';
import { apply, makePart } from '../core/commands';
import type { Placement } from '../core/types';
import type { BuzzerState, GlowState, MotorState } from '../parts/defs';
import type { Sim, SimEvent } from '../sim/sim';
import type { Cond, Level, PresetPart, TrayItem } from './types';

export function loadLevel(level: Level): { board: Board; locked: Set<string> } {
  const board = new Board(level.board.cols, level.board.rows);
  const locked = new Set<string>();
  for (const p of level.preset) {
    const part = makePart(board, p.type, p.x, p.y, p.rot, p.props);
    apply(board, { op: 'place', part });
    if (p.locked) locked.add(part.id);
  }
  return { board, locked };
}

interface EvalCtx {
  board: Board;
  sim: Sim;
  events: Set<string>;
}

const ofType = (b: Board, t: string) => [...b.parts.values()].filter((p) => p.type === t);

export function check(c: Cond, ctx: EvalCtx): boolean {
  const { board, sim } = ctx;
  if ('all' in c) return c.all.every((x) => check(x, ctx));
  if ('any' in c) return c.any.some((x) => check(x, ctx));
  if ('lit' in c) {
    const min = c.min ?? 0.45;
    const n = ofType(board, c.lit).filter((p) => (sim.state<GlowState>(p.id)?.brightness ?? 0) >= min).length;
    return c.exactly ? n === (c.count ?? 1) : n >= (c.count ?? 1);
  }
  if ('dark' in c) {
    const ps = ofType(board, c.dark);
    return ps.length > 0 && ps.every((p) => (sim.state<GlowState>(p.id)?.brightness ?? 0) < 0.15);
  }
  if ('spinning' in c) {
    const min = c.min ?? 8;
    return ofType(board, 'motor').some((p) => {
      const w = sim.state<MotorState>(p.id)?.omega ?? 0;
      return c.dir ? w * c.dir > min : Math.abs(w) > min;
    });
  }
  if ('buzzing' in c) return ofType(board, 'buzzer').some((p) => sim.state<BuzzerState>(p.id)?.sounding);
  if ('quiet' in c) return ofType(board, 'buzzer').every((p) => !sim.state<BuzzerState>(p.id)?.sounding);
  if ('switch' in c) {
    const ps = ofType(board, 'switch');
    return ps.length > 0 && ps.every((p) => !!p.props.on === (c.switch === 'on'));
  }
  if ('switchesOn' in c) return ofType(board, 'switch').filter((p) => !!p.props.on).length === c.switchesOn;
  if ('pressed' in c) return ofType(board, 'button').some((p) => !!sim.inputs.get(p.id)?.pressed);
  if ('has' in c) return ofType(board, c.has).length >= (c.count ?? 1);
  if ('event' in c) return ctx.events.has(c.event);
  return false;
}

export interface Progress {
  step: number;
  done: boolean;
  /** Step that was just completed this tick (for the ✓ animation), or -1. */
  justCompleted: number;
}

export class LevelRun {
  step = 0;
  hintsUsed = 0;
  readonly startedAt: number;
  private events = new Set<string>();
  done = false;

  constructor(
    readonly level: Level,
    now = 0,
  ) {
    this.startedAt = now;
  }

  /** Call every sim tick with that tick's events. */
  tick(board: Board, sim: Sim, events: SimEvent[]): Progress {
    for (const e of events) this.events.add(e.kind);
    let justCompleted = -1;
    if (!this.done) {
      const s = this.level.steps[this.step];
      if (s && check(s.when, { board, sim, events: this.events })) {
        justCompleted = this.step;
        this.step++;
        this.events.clear();
        if (this.step >= this.level.steps.length) this.done = true;
      }
    }
    return { step: this.step, done: this.done, justCompleted };
  }

  /** Stars: 3 for no hints, 2 for one, 1 otherwise. */
  get stars() {
    return this.hintsUsed === 0 ? 3 : this.hintsUsed === 1 ? 2 : 1;
  }

  nextHint(): string {
    const h = this.level.hints[Math.min(this.hintsUsed, this.level.hints.length - 1)] ?? '';
    this.hintsUsed++;
    return h;
  }
}

/**
 * The next part to hint at, adapted to what the kid actually built:
 *  - a part type the kid hasn't placed yet → show where the solution puts it;
 *  - the kid put their parts somewhere else (which may work fine!) → don't insist on our layout:
 *    no wire hints, the coach's spoken hints and "ready to flip" help instead.
 */
export function nextMissing(level: Level, board: Board): PresetPart | null {
  const parts = [...board.parts.values()];
  const matches = (s: PresetPart, isWire: boolean) => {
    const cellsOf = (p: {
      type: PresetPart['type'];
      x: number;
      y: number;
      rot: PresetPart['rot'];
      props: Placement['props'];
    }) =>
      footprint(p)
        .filter((c) => !isWire || c.pin >= 0)
        .map((c) => `${c.x},${c.y}`);
    const want = new Set(cellsOf({ ...s, props: s.props ?? {} }));
    return parts.some((p) => {
      if (isWire ? p.type !== 'wire' && p.type !== 'jumper' : p.type !== s.type) return false;
      const cells = cellsOf(p);
      return cells.length === want.size && cells.every((c) => want.has(c));
    });
  };
  const nonWire = level.solution.filter((s) => s.type !== 'wire');
  let custom = false;
  for (const s of nonWire) {
    if (matches(s, false)) continue;
    const want = nonWire.filter((x) => x.type === s.type).length;
    const have = parts.filter(
      (p) => p.type === s.type && !level.preset.some((q) => q.type === p.type && q.x === p.x && q.y === p.y),
    ).length;
    if (have < want) return s;
    custom = true; // placed, just not where we would have
  }
  if (custom) return null;
  for (const s of level.solution) if (s.type === 'wire' && !matches(s, true)) return s;
  return null;
}

/** How many of a tray item are left. */
export function remaining(item: TrayItem, board: Board, presetIds: Set<string>): number {
  if (item.count == null) return Infinity;
  const used = [...board.parts.values()].filter((p) => !presetIds.has(p.id) && sameItem(p, item)).length;
  return Math.max(0, item.count - used);
}

export function sameItem(p: Placement, item: TrayItem): boolean {
  // Any drawn wire (straight or bendy) uses up a "Wire" from the tray.
  if (item.type === 'wire') return p.type === 'wire' || p.type === 'jumper';
  if (p.type !== item.type) return false;
  // Colored LEDs are separate tray items.
  if (item.type === 'led' && item.props?.color) return p.props.color === item.props.color;
  return true;
}
